import { CompanyType } from 'types/join';
import { getCookie } from './setCookies';

const RAPAPORT_USER_DATA_COOKIE = 'rapaport_user_data';

/**
 * Shape of the `rapaport_user_data` cookie dropped by the pre-registration
 * site. Only a subset of these fields is consumed by the registration form;
 * the rest (utm_*, recordtypeid, leadsource, etc.) are intentionally ignored.
 */
export interface RapaportUserData {
  firstname?: string;
  lastname?: string;
  email?: string;
  phone?: string;
  company?: string;
  country?: string;
  recordtypeid?: string[];
  product_interests?: string[];
  leadsource?: string[];
  buyer_or_seller?: string;
  hubspot_company_type?: string[];
  preferred_plan?: string;
  environment?: string;
}

const tryParse = (value: string): RapaportUserData | null => {
  try {
    return JSON.parse(value) as RapaportUserData;
  } catch {
    return null;
  }
};

/**
 * Reads and parses the `rapaport_user_data` cookie. The cookie value is JSON
 * and likely percent-encoded (it contains spaces, e.g. "United States"), so we
 * attempt to decode before parsing and fall back to the raw value. Returns
 * `null` when the cookie is missing or cannot be parsed.
 */
export const getRapaportUserData = (): RapaportUserData | null => {
  const raw = getCookie(RAPAPORT_USER_DATA_COOKIE);
  if (!raw) return null;

  let decoded = raw;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    decoded = raw;
  }

  return tryParse(decoded) ?? tryParse(raw);
};

/**
 * Environment guard: only consume the cookie when its `environment` matches the
 * app's `CONFIG.__ENV` (case-insensitive). A missing `environment` is allowed
 * so older cookies do not regress.
 */
export const isUserDataForCurrentEnv = (userData: RapaportUserData): boolean => {
  const env = userData.environment;
  if (!env) return true;

  const currentEnv = typeof CONFIG !== 'undefined' ? CONFIG.__ENV : '';
  return String(env).toLowerCase() === String(currentEnv ?? '').toLowerCase();
};

const normalizeCompanyType = (value: string) => (value ?? '').toLowerCase().replace(/[_/\s]/g, '');

/**
 * Best-effort match of the cookie's company type against the CompanyTypes
 * dropdown options. Returns the matching option `name`, or '' when none match.
 */
export const matchCompanyType = (cookieType: string, companyTypes: CompanyType[]): string => {
  if (!cookieType || !companyTypes?.length) return '';

  const target = normalizeCompanyType(cookieType);
  const match = companyTypes.find(type => normalizeCompanyType(type.name) === target);
  return match?.name ?? '';
};

/**
 * Deletes the `rapaport_user_data` cookie after registration completes so it
 * cannot prefill (or suppress the HubSpot submission for) a later, unrelated
 * registration on the same machine.
 */
export const deleteRapaportUserData = (): void => {
  document.cookie = `${RAPAPORT_USER_DATA_COOKIE}=; expires=${new Date(0).toUTCString()}; path=/;`;
};
